import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/common.css';

function MissionManagement() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_required: false,
    weight: 'normal',
    requires_photo: false,
    cutoff_time: '21:00',
    schedule_days: []
  });

  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  // 미션 템플릿 데이터
  const missionTemplates = [
    {
      id: 'room_cleaning',
      title: '자기 방 정리하기',
      description: '침대 정리, 책상 정리, 바닥에 떨어진 물건들을 제자리에 놓기',
      category: '🏠 집안일',
      suggestedWeight: 'normal',
      suggestedTime: '20:00',
      requiresPhoto: true
    },
    {
      id: 'trash_disposal',
      title: '쓰레기 버리기',
      description: '방, 거실, 주방의 쓰레기통 비우고 새 비닐봉지 넣기',
      category: '🏠 집안일',
      suggestedWeight: 'low',
      suggestedTime: '19:00',
      requiresPhoto: false
    },
    {
      id: 'living_cleaning',
      title: '거실/주방 청소 돕기',
      description: '테이블 닦기, 바닥 쓸기, 소파 정리하기 등 가능한 것 돕기',
      category: '🏠 집안일',
      suggestedWeight: 'normal',
      suggestedTime: '19:30',
      requiresPhoto: true
    },
    {
      id: 'dishes_cleanup',
      title: '식사 후 그릇 치우기',
      description: '자신이 사용한 그릇과 컵을 싱크대로 가져다 놓기',
      category: '🏠 집안일',
      suggestedWeight: 'low',
      suggestedTime: '21:00',
      requiresPhoto: false
    },
    {
      id: 'homework',
      title: '숙제 잘하기',
      description: '학교 숙제를 끝까지 완성하고 학습장에 부모님 확인 받기',
      category: '📚 학습',
      suggestedWeight: 'high',
      suggestedTime: '20:00',
      requiresPhoto: true
    },
    {
      id: 'wake_up',
      title: '아침에 스스로 일어나기',
      description: '알람 소리에 혼자 일어나서 부모님 깨우지 않고 준비하기',
      category: '💪 생활습관',
      suggestedWeight: 'normal',
      suggestedTime: '08:00',
      requiresPhoto: false
    },
    {
      id: 'make_bed',
      title: '이불 개기',
      description: '일어난 후 침대 정리하고 이불을 깔끔하게 개기',
      category: '💪 생활습관',
      suggestedWeight: 'low',
      suggestedTime: '08:30',
      requiresPhoto: true
    },
    {
      id: 'school_prep',
      title: '내일 학교 준비(책가방 챙기기)',
      description: '시간표 보고 내일 필요한 책, 준비물 등을 미리 챙기기',
      category: '📚 학습',
      suggestedWeight: 'normal',
      suggestedTime: '21:30',
      requiresPhoto: false
    },
    {
      id: 'save_energy',
      title: '전등,에어컨 끄기(절약)',
      description: '사용하지 않는 방의 전등과 에어컨을 끄고 절약 실천하기',
      category: '🌱 환경',
      suggestedWeight: 'low',
      suggestedTime: '22:00',
      requiresPhoto: false
    },
    {
      id: 'water_plants',
      title: '화분에 물 주기',
      description: '집안 화분들의 흙 상태를 확인하고 필요시 물 주기',
      category: '🌱 환경',
      suggestedWeight: 'low',
      suggestedTime: '18:00',
      requiresPhoto: true
    },
    {
      id: 'laundry_help',
      title: '빨래 널기/빨래 개기',
      description: '세탁이 끝난 빨래를 널거나 마른 빨래를 개서 정리하기',
      category: '🏠 집안일',
      suggestedWeight: 'normal',
      suggestedTime: '17:00',
      requiresPhoto: false
    },
    {
      id: 'shoes_organize',
      title: '현관 신발 가지런히 놓기',
      description: '현관에 벗어둔 가족들의 신발을 깔끔하게 정리하기',
      category: '🏠 집안일',
      suggestedWeight: 'low',
      suggestedTime: '19:00',
      requiresPhoto: true
    },
    {
      id: 'help_parents',
      title: '부모님 심부름 하기',
      description: '부모님이 부탁하신 간단한 심부름을 성실히 도와드리기',
      category: '❤️ 효도',
      suggestedWeight: 'normal',
      suggestedTime: '20:00',
      requiresPhoto: false
    },
    {
      id: 'homework_complete',
      title: '숙제하기',
      description: '학교에서 내준 모든 숙제를 완성하고 정리하기',
      category: '📚 학습',
      suggestedWeight: 'high',
      suggestedTime: '19:00',
      requiresPhoto: true
    },
    {
      id: 'no_smartphone_tv',
      title: '스마트폰(TV) 안보기(2시간)',
      description: '정해진 시간 동안 스마트폰과 TV를 보지 않고 다른 활동하기',
      category: '💪 생활습관',
      suggestedWeight: 'normal',
      suggestedTime: '21:00',
      requiresPhoto: false
    },
    {
      id: 'brush_teeth',
      title: '양치질 하기',
      description: '아침, 저녁 양치질을 깨끗하게 하고 입안 관리하기',
      category: '💪 생활습관',
      suggestedWeight: 'normal',
      suggestedTime: '22:00',
      requiresPhoto: false
    },
    {
      id: 'read_book',
      title: '책 읽기(30분)',
      description: '좋아하는 책을 30분 이상 집중해서 읽기',
      category: '📚 학습',
      suggestedWeight: 'normal',
      suggestedTime: '20:30',
      requiresPhoto: false
    },
    {
      id: 'exercise',
      title: '운동하기',
      description: '스트레칭, 줄넘기, 산책 등 몸을 움직이는 활동하기',
      category: '💪 건강',
      suggestedWeight: 'normal',
      suggestedTime: '17:30',
      requiresPhoto: false
    },
    {
      id: 'greeting',
      title: '인사 잘하기',
      description: '가족, 선생님, 친구들에게 밝게 인사하기',
      category: '😊 예의',
      suggestedWeight: 'low',
      suggestedTime: '08:00',
      requiresPhoto: false
    },
    {
      id: 'diary',
      title: '일기 쓰기',
      description: '오늘 있었던 일과 느낌을 일기장에 적기',
      category: '📚 학습',
      suggestedWeight: 'low',
      suggestedTime: '21:30',
      requiresPhoto: false
    },
    {
      id: 'save_water',
      title: '물 아껴쓰기',
      description: '양치할 때, 손 씻을 때 물을 아껴쓰고 절약 실천하기',
      category: '🌱 환경',
      suggestedWeight: 'low',
      suggestedTime: '22:00',
      requiresPhoto: false
    },
    {
      id: 'pet_care',
      title: '반려동물 돌보기',
      description: '반려동물에게 밥주기, 물주기, 산책시켜주기',
      category: '🐕 반려동물',
      suggestedWeight: 'normal',
      suggestedTime: '18:00',
      requiresPhoto: true
    },
    {
      id: 'organize_desk',
      title: '책상 정리하기',
      description: '공부한 후 책상 위를 깔끔하게 정리하기',
      category: '📚 학습',
      suggestedWeight: 'low',
      suggestedTime: '21:00',
      requiresPhoto: true
    },
    {
      id: 'help_sibling',
      title: '동생(형/누나) 도와주기',
      description: '형제자매가 어려워하는 일을 도와주기',
      category: '❤️ 가족사랑',
      suggestedWeight: 'normal',
      suggestedTime: '19:30',
      requiresPhoto: false
    }
  ];

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        navigate('/');
        return;
      }
    } catch (error) {
      navigate('/parent/login');
      return;
    }

    fetchMissions();
  }, [navigate]);

  const fetchMissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v0/missions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMissions(response.data.missions);
    } catch (error) {
      setError('미션 목록을 불러오는데 실패했습니다.');
      console.error('Fetch missions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDayToggle = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(dayIndex)
        ? prev.schedule_days.filter(d => d !== dayIndex)
        : [...prev.schedule_days, dayIndex].sort()
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      is_required: false,
      weight: 'normal',
      requires_photo: false,
      cutoff_time: '21:00',
      schedule_days: []
    });
    setEditingMission(null);
  };

  const handleCreateMission = () => {
    resetForm();
    setShowTemplateModal(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      description: template.description,
      is_required: false,
      weight: template.suggestedWeight,
      requires_photo: template.requiresPhoto,
      cutoff_time: template.suggestedTime,
      schedule_days: []
    });
    setShowTemplateModal(false);
    setShowCreateModal(true);
  };

  const handleDirectCreate = () => {
    resetForm();
    setShowTemplateModal(false);
    setShowCreateModal(true);
  };

  // 가중치 변환 함수들
  const weightToText = (numWeight) => {
    if (numWeight >= 1.5) return 'high';
    if (numWeight <= 0.7) return 'low';
    return 'normal';
  };

  const textToWeight = (textWeight) => {
    switch(textWeight) {
      case 'high': return 1.5;
      case 'low': return 0.7;
      default: return 1.0;
    }
  };

  const getWeightDisplayText = (textWeight) => {
    switch(textWeight) {
      case 'high': return '상 (1.5배)';
      case 'low': return '하 (0.7배)';
      default: return '중 (1.0배)';
    }
  };

  const handleEditMission = (mission) => {
    setFormData({
      title: mission.title,
      description: mission.description || '',
      is_required: Boolean(mission.is_required),
      weight: weightToText(parseFloat(mission.weight)),
      requires_photo: Boolean(mission.requires_photo),
      cutoff_time: mission.cutoff_time.substring(0, 5), // HH:MM 형식으로 변환
      schedule_days: mission.schedule_days
    });
    setEditingMission(mission);
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.schedule_days.length === 0) {
      alert('반복 요일을 최소 1개 이상 선택해주세요.');
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // 서버로 보낼 데이터 준비 (가중치를 숫자로 변환)
      const submitData = {
        ...formData,
        weight: textToWeight(formData.weight)
      };

      if (editingMission) {
        // 수정
        await axios.patch(`/api/v0/missions/${editingMission.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('미션이 수정되었습니다.');
      } else {
        // 생성
        await axios.post('/api/v0/missions', submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('미션이 생성되었습니다.');
      }

      setShowCreateModal(false);
      resetForm();
      fetchMissions();
    } catch (error) {
      alert(error.response?.data?.error || '미션 처리 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteMission = async (missionId, missionTitle) => {
    if (!window.confirm(`'${missionTitle}' 미션을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v0/missions/${missionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('미션이 삭제되었습니다.');
      fetchMissions();
    } catch (error) {
      alert(error.response?.data?.error || '미션 삭제 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '20px' }}>
            로딩 중...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>🎯 미션 관리</h1>
        <button
          onClick={handleCreateMission}
          className="btn btn-primary"
        >
          + 새 미션
        </button>
      </div>

      {/* 미션 목록 */}
      <div className="missions-container">
        {error && <div className="error">{error}</div>}

        {missions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎯</div>
            <h3>아직 미션이 없습니다</h3>
            <p>첫 번째 미션을 만들어보세요!</p>
            <button
              onClick={handleCreateMission}
              className="btn btn-primary"
            >
              미션 만들기
            </button>
          </div>
        ) : (
          <div className="missions-grid">
            {missions.map(mission => (
              <div key={mission.id} className="mission-card">
                <div className="mission-header">
                  <h3>{mission.title}</h3>
                  <div className="mission-badges">
                    {mission.is_required && <span className="badge badge-required">필수</span>}
                    {mission.requires_photo && <span className="badge badge-photo">📷</span>}
                  </div>
                </div>

                {mission.description && (
                  <p className="mission-description">{mission.description}</p>
                )}

                <div className="mission-details">
                  <div className="detail-item">
                    <span className="detail-label">반복 요일:</span>
                    <span className="detail-value">
                      {mission.schedule_days.map(day => dayNames[day]).join(', ')}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">마감 시간:</span>
                    <span className="detail-value">{mission.cutoff_time.substring(0, 5)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">가중치:</span>
                    <span className="detail-value">{getWeightDisplayText(weightToText(parseFloat(mission.weight)))}</span>
                  </div>
                </div>

                <div className="mission-actions">
                  <button
                    onClick={() => handleEditMission(mission)}
                    className="btn btn-outline btn-sm"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleDeleteMission(mission.id, mission.title)}
                    className="btn btn-danger btn-sm"
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 템플릿 선택 모달 */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>미션 만들기</h2>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="template-modal-body">
              <div className="template-options">
                <div className="option-header">
                  <h3>📋 템플릿에서 선택</h3>
                  <p>자주 사용하는 미션을 빠르게 만들어보세요</p>
                </div>

                <div className="template-categories">
                  {Object.entries(
                    missionTemplates.reduce((acc, template) => {
                      if (!acc[template.category]) acc[template.category] = [];
                      acc[template.category].push(template);
                      return acc;
                    }, {})
                  ).map(([category, templates]) => (
                    <div key={category} className="template-category">
                      <h4 className="category-title">{category}</h4>
                      <div className="template-list">
                        {templates.map(template => (
                          <button
                            key={template.id}
                            className="template-item"
                            onClick={() => handleTemplateSelect(template)}
                          >
                            <div className="template-title">{template.title}</div>
                            <div className="template-description">{template.description}</div>
                            <div className="template-meta">
                              {template.requiresPhoto && <span className="template-photo">📷 사진필요</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="direct-option">
                  <div className="option-divider">
                    <span>또는</span>
                  </div>
                  <button
                    className="direct-create-btn"
                    onClick={handleDirectCreate}
                  >
                    <div className="direct-icon">✏️</div>
                    <div className="direct-content">
                      <h4>직접 작성하기</h4>
                      <p>우리 가족만의 특별한 미션을 만들어보세요</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 미션 생성/수정 모달 */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMission ? '미션 수정' : '새 미션 만들기'}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label className="form-label">미션 제목 *</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  maxLength="200"
                  placeholder="예: 방 정리하기"
                />
              </div>

              <div className="form-group">
                <label className="form-label">미션 설명</label>
                <textarea
                  name="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength="1000"
                  placeholder="미션에 대한 자세한 설명을 입력해주세요"
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">가중치 *</label>
                  <select
                    name="weight"
                    className="form-input"
                    value={formData.weight}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="low">하 (0.7배) - 덜 중요한 미션</option>
                    <option value="normal">중 (1.0배) - 보통 중요도</option>
                    <option value="high">상 (1.5배) - 매우 중요한 미션</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">마감 시간 *</label>
                  <input
                    type="time"
                    name="cutoff_time"
                    className="form-input"
                    value={formData.cutoff_time}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">반복 요일 *</label>
                <div className="day-selector">
                  {dayNames.map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`day-btn ${formData.schedule_days.includes(index) ? 'active' : ''}`}
                      onClick={() => handleDayToggle(index)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-text">필수 미션 (실패 시 용돈 감액)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="requires_photo"
                    checked={formData.requires_photo}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-text">증빙 사진 필수</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-outline"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingMission ? '수정하기' : '만들기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: white;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }


        .page-header h1 {
          margin: 0;
          color: #333;
          font-size: 1.5rem;
        }

        .missions-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #333;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #666;
          margin-bottom: 30px;
        }

        .missions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .mission-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .mission-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .mission-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
        }

        .mission-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.2rem;
          flex: 1;
        }

        .mission-badges {
          display: flex;
          gap: 5px;
          margin-left: 10px;
        }

        .badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: bold;
        }

        .badge-required {
          background: #ff4757;
          color: white;
        }

        .badge-photo {
          background: #feca57;
          color: white;
        }

        .mission-description {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .mission-details {
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .detail-label {
          color: #888;
          font-size: 0.9rem;
        }

        .detail-value {
          color: #333;
          font-weight: 500;
        }

        .mission-actions {
          display: flex;
          gap: 10px;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 0.9rem;
        }

        .btn-danger {
          background: #ff4757;
          color: white;
          border: none;
        }

        .btn-danger:hover {
          background: #ff3742;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 15px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 25px;
          border-bottom: 1px solid #e0e0e0;
        }

        .modal-header h2 {
          margin: 0;
          color: #333;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #666;
        }

        .modal-form {
          padding: 25px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 500;
        }

        .form-input, .form-textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
        }

        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .day-selector {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .day-btn {
          padding: 10px 16px;
          border: 2px solid #ddd;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .day-btn.active {
          background: #8B4513;
          border-color: #8B4513;
          color: white;
        }

        .form-checkboxes {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-bottom: 20px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
        }

        .checkbox-label input {
          margin-right: 10px;
        }

        .checkbox-text {
          color: #333;
        }

        .modal-actions {
          display: flex;
          gap: 15px;
          justify-content: flex-end;
          margin-top: 30px;
        }

        /* 템플릿 모달 스타일 */
        .template-modal {
          max-width: 900px;
          max-height: 90vh;
        }

        .template-modal-body {
          padding: 0;
          max-height: calc(90vh - 120px);
          overflow-y: auto;
        }

        .template-options {
          padding: 25px;
        }

        .option-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .option-header h3 {
          color: #333;
          margin-bottom: 8px;
          font-size: 1.2rem;
        }

        .option-header p {
          color: #666;
          font-size: 0.95rem;
        }

        .template-categories {
          margin-bottom: 40px;
        }

        .template-category {
          margin-bottom: 25px;
        }

        .category-title {
          color: #333;
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 15px;
          padding-left: 5px;
        }

        .template-list {
          display: grid;
          gap: 12px;
        }

        .template-item {
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          padding: 15px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s;
          width: 100%;
        }

        .template-item:hover {
          border-color: #8B4513;
          background: #f0f0f0;
          transform: translateY(-1px);
        }

        .template-title {
          font-weight: 600;
          color: #333;
          margin-bottom: 6px;
          font-size: 1rem;
        }

        .template-description {
          color: #666;
          font-size: 0.85rem;
          line-height: 1.4;
          margin-bottom: 10px;
        }

        .template-meta {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .template-weight {
          background: #e3f2fd;
          color: #1976d2;
          padding: 2px 8px;
          border-radius: 8px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .template-time {
          color: #666;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .template-photo {
          font-size: 0.9rem;
        }

        .direct-option {
          border-top: 1px solid #e9ecef;
          padding-top: 25px;
        }

        .option-divider {
          text-align: center;
          margin-bottom: 20px;
          position: relative;
        }

        .option-divider:before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #e9ecef;
        }

        .option-divider span {
          background: white;
          color: #666;
          padding: 0 15px;
          font-size: 0.9rem;
          position: relative;
          z-index: 1;
        }

        .direct-create-btn {
          width: 100%;
          background: white;
          border: 2px dashed #8B4513;
          border-radius: 12px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .direct-create-btn:hover {
          background: #faf5f0;
          border-color: #6d3610;
        }

        .direct-icon {
          font-size: 2rem;
        }

        .direct-content h4 {
          margin: 0 0 5px 0;
          color: #333;
          font-size: 1.1rem;
        }

        .direct-content p {
          margin: 0;
          color: #666;
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .missions-grid {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .day-selector {
            justify-content: center;
          }

          .page-header {
            flex-direction: column;
            gap: 15px;
          }

          .template-modal {
            max-width: 95%;
          }

          .direct-create-btn {
            flex-direction: column;
            text-align: center;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
}

export default MissionManagement;